import { z } from 'zod';

/**
 * The Backoffice role ladder, mirroring `apps/api`'s `domain/operator-role.ts`.
 *
 * Duplicated rather than imported because the two apps do not share a package —
 * the same reason `operatorSchema` below re-declares the operator shape. The
 * order is the type, so if the API's ladder ever changes, this array is the
 * thing to change with it.
 *
 * **Nothing here is a security boundary.** Every one of these checks decides
 * whether to render a button; the API decides whether the write happens, and it
 * does not trust this file or the bundle it ships in.
 */
export const OPERATOR_ROLES = ['read-only', 'ops-csm', 'engineer', 'platform-admin'] as const;

export type OperatorRole = (typeof OPERATOR_ROLES)[number];

/** Does `role` clear the bar `minimum` sets? A minimum, never an exact match. */
export function atLeast(role: OperatorRole, minimum: OperatorRole): boolean {
    return OPERATOR_ROLES.indexOf(role) >= OPERATOR_ROLES.indexOf(minimum);
}

/** How a tier is named to the operator holding it. */
export const ROLE_LABEL: Record<OperatorRole, string> = {
    'read-only': 'Read-only',
    'ops-csm': 'Ops / CSM',
    engineer: 'Engineer',
    'platform-admin': 'Platform admin',
};

/**
 * The employee behind the request, as far as the Backoffice UI is concerned.
 *
 * Mirrors `apps/api`'s `Operator` domain type, which is what `GET /api/auth/me`
 * serialises. It is parsed rather than cast: this is a network boundary now, not
 * a server-side function call, so the shape is checked at runtime like every
 * other API response.
 */
export const operatorSchema = z.object({
    id: z.string().min(1),
    name: z.string(),
    email: z.string(),
    avatarUrl: z.string().optional(),
    /** Atelier hub admin — display only. It is not a Backoffice permission. */
    isAtelierAdmin: z.boolean(),
    /**
     * The Backoffice tier (NOXSCRUM-1436).
     *
     * `.catch` rather than a strict parse, and read-only rather than a throw:
     * an API that adds a fifth tier would otherwise fail this schema, and this
     * schema gates the whole app — every employee would be locked out of the UI
     * by a field they only use to grey out buttons. Degrading to the lowest
     * tier keeps the app usable and keeps the failure safe, and the writes it
     * hides would be refused by the API anyway.
     */
    role: z.enum(OPERATOR_ROLES).catch('read-only'),
});

export type Operator = z.infer<typeof operatorSchema>;

/** Envelope returned by `GET /api/auth/me`. */
export const sessionResponseSchema = z.object({ operator: operatorSchema });
